/**
 * Accessibility scaffolding shared by both layouts:
 * - a skip-to-content link (first tab stop) that jumps past the header/tab bar,
 * - a <main> landmark as its target,
 * - focus moved to <main> on each route change so screen-reader and keyboard
 *   users land on the new page instead of staying on a stale control.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useT } from "../i18n/LocaleProvider.js";

export function MainRegion({ children }: { children: ReactNode }) {
  const t = useT();
  const { pathname } = useLocation();
  const ref = useRef<HTMLElement>(null);
  const firstRender = useRef(true);

  useEffect(() => {
    // Skip the initial mount so the skip-link stays the first tab stop on load;
    // only move focus to <main> on subsequent navigations (announce the new page).
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    ref.current?.focus();
  }, [pathname]);

  return (
    <>
      <a className="skip-link" href="#main">
        {t.common.skipToContent}
      </a>
      <main id="main" ref={ref} tabIndex={-1}>
        {children}
      </main>
    </>
  );
}
